import type { Blueprint } from 'types/api.types';
import { getRepositoryUrl } from './vcs.utils';

const AzureDevopsDomain = 'dev.azure.com';
const BitbucketDomain = 'bitbucket.org';
const GithubDomain = 'github.com';
const GitlabDomain = 'gitlab.com';

const getBitbucketLines = (startLine?: number, endLine?: number): string => {
  if (!startLine) return '';
  const lineFragment = endLine && endLine !== startLine ? `${startLine}:${endLine}` : `${startLine}`;
  return `#lines-${lineFragment}`;
};

const getAzureDevOpsLines = (startLine?: number, endLine?: number): string => {
  if (!startLine) return '';
  return endLine && endLine !== startLine ? `&line=${startLine}&lineEnd=${endLine}` : `&line=${startLine}`;
};

const getGitHubGitLabLines = (startLine?: number, endLine?: number): string => {
  if (!startLine) return '';
  return endLine && endLine !== startLine ? `#L${startLine}-L${endLine}` : `#L${startLine}`;
};

export const getTemplateProjectScopeId = (template?: Blueprint): string | undefined =>
  template?.creationScope === 'PROJECT' ? template.creationScopeId : undefined;

export const buildRepoUrl = ({
  repository,
  revision,
  url,
  path,
  isTag,
  startLine,
  endLine
}: {
  repository: string;
  revision: string;
  url?: string;
  path?: string;
  isTag: boolean;
  startLine?: number;
  endLine?: number;
}) => {
  if (repository.includes(BitbucketDomain)) {
    return `${url}${revision ? `/src/${revision}/${path}` : ''}${getBitbucketLines(startLine, endLine)}`;
  } else if (repository.includes(AzureDevopsDomain)) {
    return `${url}?path=/${path}&version=${isTag ? 'GT' : 'GB'}${revision}${getAzureDevOpsLines(startLine, endLine)}`;
  } else {
    return `${url}${revision ? `/tree/${revision}/${path}` : ''}${getGitHubGitLabLines(startLine, endLine)}`;
  }
};

export const getGitRevisionUrl = ({
  repository,
  revision,
  path,
  isTag = false
}: {
  repository: string;
  revision?: string;
  path?: string;
  isTag?: boolean;
}) => {
  const url = getRepositoryUrl(repository);
  const fixedRevision = revision ?? (repository.includes(AzureDevopsDomain) ? 'main' : path ? 'master' : '');
  const fixedPath = path ?? '';
  const builtUrl = buildRepoUrl({ repository, revision: fixedRevision, url, path: fixedPath, isTag });
  return builtUrl.endsWith('/') || repository.includes(AzureDevopsDomain) ? builtUrl : `${builtUrl}/`;
};

export const getGitPrUrl = (repository: string, prNumber: string) => {
  const url = getRepositoryUrl(repository);
  const separator = repository.includes(GithubDomain)
    ? 'pull'
    : repository.includes(GitlabDomain)
      ? '-/merge_requests'
      : repository.includes(BitbucketDomain)
        ? 'pull-requests'
        : repository.includes(AzureDevopsDomain)
          ? 'pullrequest'
          : '';
  const builtUrl = [url, separator, prNumber].filter(_ => _).join('/');

  return builtUrl.endsWith('/') ? builtUrl : `${builtUrl}/`;
};

export const getGitCommitUrl = (blueprint: Blueprint, commitHash: string) => {
  const url = getRepositoryUrl(blueprint.repository);
  const separator =
    blueprint.isGitHubEnterprise || blueprint.githubInstallationId
      ? 'commit'
      : blueprint.isGitLabEnterprise || blueprint.isGitLab
        ? '-/commit'
        : blueprint.isBitbucketServer || blueprint.bitbucketClientKey
          ? 'commits'
          : blueprint.isAzureDevOps
            ? 'commit'
            : '';
  const builtUrl = [url, separator, commitHash].filter(_ => _).join('/');

  return builtUrl.endsWith('/') ? builtUrl : `${builtUrl}/`;
};

export const getGitCommitDiffUrl = ({
  blueprint,
  baseCommitHash,
  headCommitHash
}: {
  blueprint: Blueprint;
  baseCommitHash: string;
  headCommitHash: string;
}) => {
  const url = getRepositoryUrl(blueprint.repository);

  const diffHash =
    blueprint.isGitHubEnterprise || blueprint.githubInstallationId
      ? `compare/${baseCommitHash}...${headCommitHash}`
      : blueprint.isGitLabEnterprise || blueprint.isGitLab
        ? `-/compare/${baseCommitHash}...${headCommitHash}`
        : blueprint.isBitbucketServer || blueprint.bitbucketClientKey
          ? `branches/compare/${baseCommitHash}..${headCommitHash}`
          : blueprint.isAzureDevOps
            ? `/branchCompare?baseVersion=GC${baseCommitHash}&targetVersion=GC${headCommitHash}` // GC = Git Commit. Needs to be changed if this code ever supports branches/tags
            : '';
  const builtUrl = [url, diffHash].filter(_ => _).join('/');

  // Azure DevOps link MUST NOT end with '/' :facepalm:
  // But others (Bitbucket for sure) has to end with '/'
  return builtUrl.endsWith('/') || blueprint.isAzureDevOps ? builtUrl : `${builtUrl}/`;
};
